import produce from 'immer';
import {useEffect, useRef} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {
  CheckEntryResponseState,
  CheckEntryResultUsefulState,
  DisplayCheckEntryResults,
} from 'notebook/ai-tools/NotebookAIToolsConstants';
import {checkEntryUsingAI, CheckEntryUsingAIResponse} from 'notebook/ai-tools/NotebookAIToolsDAO';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

interface CheckEntryState {
  // Network request state.
  isLoading: boolean;
  isError: boolean;
  response: CheckEntryUsingAIResponse | null;
  // UI state
  completedStates: {[index: number]: boolean | undefined};
  usefulStates: {[index: number]: CheckEntryResultUsefulState};
}

const DEFAULT_STATE: CheckEntryState = {
  isLoading: false,
  isError: false,
  response: null,
  completedStates: {},
  usefulStates: {},
};

const checkEntryResponseState = makeUseGlobalClientState<CheckEntryState>(DEFAULT_STATE);

interface CheckEntryResultInfo {
  checkEntryResults: DisplayCheckEntryResults;
  onToggleCompleted: (index: number) => void;
  onToggleUsefulState: (index: number, value: CheckEntryResultUsefulState) => void;
  onRecompute: () => Promise<void>;
}

/**
 * Load AI notebook checker results for this entry and manage user-facing state.
 *
 * The entry is checked on initial mount of the component (i.e. when the review tab is opened directly
 * or when the user clicks a button to check the entry), and on explicit refresh from the user, and
 * kept stable aside from those events. In the future we may want to explore more continuous checking,
 * but this approach allows the user to use the results like a checklist.
 */
export default function useCheckEntryResultInfo(entryId: string): CheckEntryResultInfo {
  const [checkEntryState, setCheckEntryState] = checkEntryResponseState.useGlobalClientState(entryId);
  const needsInitialLoad =
    !checkEntryState.isLoading && !checkEntryState.isError && !checkEntryState.response;
  // Extra safeguard to avoid a fetch loop: only run the initial load once. This is already implied
  // by needsInitialLoad, but easier to manually confirm.
  const hasLoaded = useRef(false);

  useEffect(() => {
    (async () => {
      if (needsInitialLoad && !hasLoaded.current) {
        hasLoaded.current = true;
        await checkEntry();
      }
    })();
  });

  async function checkEntry(): Promise<void> {
    setCheckEntryState({...DEFAULT_STATE, isLoading: true});
    let response;
    try {
      response = await checkEntryUsingAI(entryId);
    } catch (e) {
      setCheckEntryState({...DEFAULT_STATE, isError: true});
      throw e;
    }
    setCheckEntryState({...DEFAULT_STATE, response});
  }

  function onToggleCompleted(index: number): void {
    const newValue = !checkEntryState.completedStates[index];
    trackEvent(ELN_ANALYTICS_ACTIONS.MARKED_AI_NOTEBOOK_CHECK_RESULT_COMPLETED, {
      traceId: checkEntryState.response?.traceId,
      checkResultIndex: index,
      entryId,
      newValue,
    });
    // Note: this can be a little fragile after if we somehow have an old callback run after re-fetch.
    // Since we'll switch to loading and back on re-fetch, it seems relatively safe to assume that
    // won't happen, but this could be made more robust by letting setCheckEntryState take an updater
    // function (and refusing if traceId has changed) or storing state keyed off of traceId.
    setCheckEntryState(
      produce(checkEntryState, (draft) => {
        draft.completedStates[index] = newValue;
      })
    );
  }

  function onToggleUsefulState(index: number, value: CheckEntryResultUsefulState): void {
    const newValue =
      value === checkEntryState.usefulStates[index] ? CheckEntryResultUsefulState.UNMARKED : value;
    trackEvent(ELN_ANALYTICS_ACTIONS.MARKED_AI_NOTEBOOK_CHECK_RESULT_USEFUL, {
      traceId: checkEntryState.response?.traceId,
      checkResultIndex: index,
      entryId,
      newValue,
    });
    setCheckEntryState(
      produce(checkEntryState, (draft) => {
        draft.usefulStates[index] = newValue;
      })
    );
  }

  return {
    checkEntryResults: formatCheckEntryResultsForDisplay(checkEntryState),
    onRecompute: checkEntry,
    onToggleCompleted,
    onToggleUsefulState,
  };
}

/**
 * Given the raw normalized state of check entry results, prepare it into a format that's easier to send
 * down as React props.
 */
function formatCheckEntryResultsForDisplay(checkEntryState: CheckEntryState): DisplayCheckEntryResults {
  if (checkEntryState.response && !checkEntryState.response.hadError) {
    return {
      state: CheckEntryResponseState.SUCCESS,
      traceId: checkEntryState.response.traceId,
      results: checkEntryState.response.checkResults.map((checkResult, i) => ({
        text: checkResult.text,
        isCompleted: Boolean(checkEntryState.completedStates[i]),
        usefulState: checkEntryState.usefulStates[i] ?? CheckEntryResultUsefulState.UNMARKED,
      })),
    };
  } else if (checkEntryState.isError || checkEntryState.response?.hadError) {
    return {state: CheckEntryResponseState.ERROR};
  } else {
    // We might be in a completely initial state (i.e. will load soon) or in an explicit loading state,
    // and in any case, show loading to the user.
    return {state: CheckEntryResponseState.LOADING};
  }
}
